// @flow
import * as React from 'react';
import { useState } from 'react';
import { Avatar, Box, MenuItem, Stack, Typography } from '@mui/material';
import { SelectInput } from '@coverright/ui/inputs';
import { NoData, Portal, useQaQuestionPerformanceQuery } from '../../../shared';
import { Preloader } from '@coverright/features';
import _ from 'lodash';
import { scoreColor } from '../ui/qa-shared';
import { QaTrendChart } from '../ui/qa-trend-chart';

const QUESTIONS = Array.from({ length: 30 }, (_, i) => `Q${i + 1}`);
// fail-rate color: low fail = green, high fail = red (inverse of score).
// failRate is already a 0-100 percentage from the backend.
const failColor = (rate: number) => scoreColor(100 - rate);

export const QuestionPerformance = () => {
  const [questionId, setQuestionId] = useState('Q1');
  const { data, loading } = useQaQuestionPerformanceQuery({
    variables: { questionId },
    fetchPolicy: 'cache-and-network',
  });
  const perf = data?.qaQuestionPerformance;

  return (
    <Stack gap={3}>
      <Box maxWidth={360}>
        <Typography className={'text-input-label'} sx={{ lineHeight: '19px', fontWeight: 600, mb: 0.5 }}>Question</Typography>
        <SelectInput hideTick value={questionId} onChange={e => setQuestionId(e.target.value as string)}>
          {QUESTIONS.map(q => <MenuItem key={q} value={q}>{q}</MenuItem>)}
        </SelectInput>
      </Box>

      {loading && !data && <Preloader sx={{ height: 250 }} />}
      {perf && (
        <>
          <Stack direction={'row'} gap={3} flexWrap={'wrap'}>
            <Stack direction={'row'} gap={2} flexWrap={'wrap'} minWidth={280}>
              <Stat label={'Team fail rate'} value={`${Math.round(perf.failRate)}%`} color={failColor(perf.failRate)} />
              <Stat label={'Points lost'} value={perf.pointsLost} />
              <Stat label={'Applicable'} value={`${perf.totalApplicable}`} suffix={'calls'} />
              <Stat label={'Excluded N/A'} value={perf.excludedNA} />
            </Stack>
            <Portal p={2} flex={1} minWidth={320}>
              <Stack direction={'row'} alignItems={'center'} mb={1}>
                <Typography variant={'subtitle1'} flex={1}>Fail rate trend</Typography>
                <Typography variant={'caption'} color={'text.secondary'}>Per ISO week</Typography>
              </Stack>
              {perf.trend.length
                ? <QaTrendChart points={perf.trend} fill color={'#F14E42'} />
                : <NoData height={120}>No trend yet</NoData>}
            </Portal>
          </Stack>

          <Portal p={2}>
            <Typography variant={'subtitle1'} mb={2}>Per-agent breakdown</Typography>
            {perf.perAgent.length ? (
              <Stack gap={1.5}>
                {_.orderBy(perf.perAgent, 'failRate', 'desc').map(a => (
                  <Box key={a.agentId}>
                    <Stack direction={'row'} alignItems={'center'} gap={1} mb={0.5}>
                      <Avatar sx={{ width: 24, height: 24, bgcolor: '#2d969f', fontSize: 11 }}>
                        {(a.agentName || '?').split(' ').map(w => w[0]).slice(0, 2).join('')}
                      </Avatar>
                      <Typography variant={'body2'} flex={1}>{a.agentName || 'Unknown'}</Typography>
                      <Typography variant={'body2'} sx={{ color: failColor(a.failRate), fontWeight: 600 }}>
                        {Math.round(a.failRate)}% fail
                      </Typography>
                    </Stack>
                    <Box sx={{ height: 8, bgcolor: '#F3F4F6', borderRadius: 4, overflow: 'hidden' }}>
                      <Box sx={{ height: 1, width: `${Math.round(a.failRate)}%`, bgcolor: failColor(a.failRate), borderRadius: 4 }} />
                    </Box>
                  </Box>
                ))}
              </Stack>
            ) : <NoData height={100}>No agent data</NoData>}
          </Portal>
        </>
      )}
    </Stack>
  );
};

const Stat = ({ label, value, suffix, color }: { label: string; value: React.ReactNode; suffix?: string; color?: string }) => (
  <Portal p={2} sx={{ minWidth: 130, flex: 1 }}>
    <Typography variant={'caption'} color={'text.secondary'}>{label}</Typography>
    <Typography variant={'h3'} sx={{ color }}>{value} {suffix && <Typography component={'span'} variant={'caption'} color={'text.secondary'}>{suffix}</Typography>}</Typography>
  </Portal>
);